import React from 'react'

import { NotificationInfoFragment } from '@sketch/gql-types'

import { AddShareMemberPreview } from './AddShareMemberPreview'
import { CommentMentionPreview } from './CommentMentionPreview'
import { DownloadableAssetPreview } from './DownloadableAssetPreview'
import { FolderNotificationPreview } from './FolderNotificationPreview'
import { ArchiveIcon, TrashIcon } from './FolderNotificationPreview.styles'
import { StarPreview } from './StarPreview'
import { WorkspaceInviteAcceptedPreview } from './WorkspaceInviteAcceptedPreview'
import { WorkspaceInvitedPreview } from './WorkspaceInvitedPreview'
import { RichPreviewBox, RichPreviewText } from './InboxPreview.styles'

interface InboxPreviewBodyProps {
  notification: NotificationInfoFragment
}

export const InboxPreviewBody = ({ notification }: InboxPreviewBodyProps) => {
  if (
    notification.__typename === 'CommentNotification' ||
    notification.__typename === 'MentionNotification'
  ) {
    return <CommentMentionPreview notification={notification} />
  }

  if (notification.__typename === 'DownloadableAssetRenderedNotification') {
    return <DownloadableAssetPreview notification={notification} />
  }

  if (notification.__typename === 'AddShareMemberNotification') {
    return <AddShareMemberPreview notification={notification} />
  }

  if (notification.__typename === 'StarNotification') {
    return <StarPreview notification={notification} />
  }

  if (
    notification.__typename === 'FolderArchivedNotification' ||
    notification.__typename === 'FolderTrashedNotification'
  ) {
    // Archived and trashed folders still exist, so we label the tile with the
    // folder name; a missing folder leaves nothing to preview.
    if (!notification.folder) return null

    const glyph =
      notification.__typename === 'FolderArchivedNotification' ? (
        <ArchiveIcon />
      ) : (
        <TrashIcon />
      )

    return (
      <FolderNotificationPreview
        glyph={glyph}
        folderName={notification.folder.name}
        workspace={notification.workspace}
      />
    )
  }

  if (notification.__typename === 'FolderPermanentlyDeletedNotification') {
    // A permanently-deleted folder no longer exists, so the type doesn't expose
    // the project (and therefore its name). The folder name is still carried on
    // the notification's title, so we fall back to that to label the tile.
    return (
      <FolderNotificationPreview
        glyph={<TrashIcon />}
        workspace={notification.workspace}
        folderName={notification.title}
      />
    )
  }

  if (notification.__typename === 'WorkspaceInvitedNotification') {
    return <WorkspaceInvitedPreview notification={notification} />
  }

  if (notification.__typename === 'WorkspaceInviteAcceptedNotification') {
    return <WorkspaceInviteAcceptedPreview notification={notification} />
  }

  return (
    <RichPreviewBox>
      <RichPreviewText>Rich Summary / Preview</RichPreviewText>
    </RichPreviewBox>
  )
}
